import type { FaxTransmission } from "@emr/contracts";

/** Shared by referral sending, the fax workspace and Attention. */
const failureMessages: Readonly<Record<string, string>> = {
  FAX_POLICY_BLOCKED:
    "This test system can only fax the test number. Change to the test clinic, or ask your administrator to check the test fax settings.",
  PROVIDER_UNAVAILABLE: "The fax service is unavailable. Try again in a few minutes.",
  PROVIDER_REJECTED:
    "The fax service could not send this package. Check the clinic's fax number, then try again or change clinic.",
  PROVIDER_REJECTED_CALLER_ID:
    "The fax service did not accept our clinic's sender number. Ask your administrator to correct the fax settings, then try again.",
  PROVIDER_REJECTED_SENDER_EMAIL:
    "The fax service did not accept our clinic's sender email. Ask your administrator to correct the fax settings, then try again.",
  PROVIDER_REJECTED_AUTHENTICATION:
    "The fax service could not sign in. Ask your administrator to reconnect the fax service, then try again.",
  PROVIDER_REJECTED_RECIPIENT:
    "The fax service did not accept the destination number. Check the number or change clinic, then try again.",
  PROVIDER_REJECTED_ATTACHMENT:
    "The fax service could not read the package. Ask your administrator to check the PDF before trying again.",
  PROVIDER_RESPONSE_INVALID:
    "The fax service returned an unreadable response. Check the transmission in Attention → Faxes before trying again.",
  TRANSMISSION_UNCERTAIN:
    "Delivery has not been confirmed. Check the transmission in Attention → Faxes before sending another copy.",
  FAX_PARTIALLY_SENT:
    "Some pages may have reached the clinic. Check the transmission in Attention → Faxes before sending another copy.",
  RECIPIENT_CHANGED:
    "The clinic's fax details changed before sending. Choose the clinic again, check the number, then try again.",
  SOURCE_UNAVAILABLE_OR_CHANGED:
    "A document in the package changed or is unavailable. Ask the clinician to review the package before sending again.",
  REFERRAL_BINDING_CHANGED:
    "The approved package or clinic details changed before sending. Refresh the referral and check its approval and destination before trying again.",
  SCAN_UNAVAILABLE:
    "The document safety check is temporarily unavailable. Try again in a few minutes.",
  MALWARE_DETECTED:
    "The package failed its document safety check. Ask your administrator to inspect the source documents before sending again.",
  UNSAFE_FILE:
    "The package could not pass its document safety check. Ask your administrator to inspect the PDF before sending again.",
  FILE_LIMIT_EXCEEDED:
    "The package is too large to fax. Ask the clinician to review the attachments before sending again.",
  PROCESSING_FAILED:
    "The package could not be prepared for faxing. Try again; if it fails again, contact your administrator.",
};

export const referralFaxFailureMessage = (code: string | null | undefined): string =>
  (code ? failureMessages[code] : undefined) ??
  "The fax could not be delivered. Check the clinic's fax number, then try again or change clinic.";

export function referralFaxStatus(transmission: FaxTransmission | null): {
  label: string;
  detail: string;
} {
  switch (transmission?.status) {
    case "queued":
      return { label: "Sending", detail: "The fax is queued for sending." };
    case "submitting":
      return { label: "Sending", detail: "The package is being sent to the fax service." };
    case "submitted":
      return {
        label: "Sending",
        detail: "Waiting for the clinic's fax machine to confirm delivery.",
      };
    case "delivered":
      return {
        label: "Sent (delivered)",
        detail: "The clinic's fax machine confirmed delivery. Booking is tracked separately.",
      };
    case "failed":
      return { label: "Failed", detail: referralFaxFailureMessage(transmission.failureCode) };
    case "uncertain":
      return {
        label: "Delivery unconfirmed",
        detail: referralFaxFailureMessage(transmission.failureCode ?? "TRANSMISSION_UNCERTAIN"),
      };
    case "cancel_requested":
      return {
        label: "Cancellation requested",
        detail: "Waiting for cancellation to be confirmed. Do not send another copy yet.",
      };
    case "cancelled":
      return {
        label: "Not sent",
        detail: "The fax was cancelled. Check the clinic before sending again.",
      };
    default:
      return {
        label: "Not sent",
        detail: "Check the clinic and fax number, then send the package.",
      };
  }
}
